import { useLayoutEffect } from 'react';

/**
 * Gives every element matching `selector` inside a container the width of the
 * widest one, by measuring them and publishing the result as a CSS custom
 * property on the container (the stylesheet applies it as a min-width).
 *
 * For items that sit in different rows or parents, where no CSS layout can
 * line their widths up — e.g. the rate separator pills in the guest time
 * slot grid, one per grid row.
 *
 * Re-measures when the container resizes (rotation, a narrower phone wrapping
 * the text) and once web fonts finish loading, since both change the natural
 * widths.
 *
 * @param {{current: HTMLElement|null}} containerRef - element the items live in
 * @param {string} selector - CSS selector for the items to match
 * @param {string} propertyName - custom property to set, e.g. '--tss-rate-pill-w'
 * @param {Array<unknown>} deps - re-measure when these change (the items' content)
 * @returns {void}
 */
function useMatchedWidths(containerRef, selector, propertyName, deps) {
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let lastWidth = null;
    const measure = () => {
      // Clear first so each item is measured at its natural width, not at the
      // width the previous pass forced on it — otherwise it could only grow.
      container.style.removeProperty(propertyName);
      let widest = 0;
      container.querySelectorAll(selector).forEach((item) => {
        widest = Math.max(widest, item.getBoundingClientRect().width);
      });
      if (widest > 0) container.style.setProperty(propertyName, `${Math.ceil(widest)}px`);
    };

    measure();

    // Width changes only: the forced min-width never changes the container's
    // own width, so this can't feed back into itself.
    let observer = null;
    if (typeof ResizeObserver === 'function') {
      observer = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect?.width;
        if (width === lastWidth) return;
        lastWidth = width;
        measure();
      });
      observer.observe(container);
    }

    let cancelled = false;
    document.fonts?.ready?.then(() => { if (!cancelled) measure(); });

    return () => {
      cancelled = true;
      observer?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export default useMatchedWidths;
